import type {
    PpaFilterCategory,
    PpaSimulationMetaObject,
    PpaSimulationMetaResponse,
    SimulationColumnFilterSortConfig,
    SimulationFilterConfig,
    SimulationFilterEditedAction,
    SimulationFilterFooterAction,
    SimulationFilterShowOnlyItem,
    SimulationTableColumnMeta,
    SimulationTableConfig,
    SimulationToolbarAction,
    SimulationToolbarConfig,
} from 'types/ppaSimulationMeta';

function toSimulatorFilterAttributeType(id: string): string {
    return id
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join('_');
}

export function findMetaObjectById(
    objects: PpaSimulationMetaObject[] | undefined,
    id: string,
): PpaSimulationMetaObject | undefined {
    if (!objects?.length) return undefined;

    for (const obj of objects) {
        if (obj.id === id) return obj;
        const nested = findMetaObjectById(obj.objects, id);
        if (nested) return nested;
    }

    return undefined;
}

function toToolbarAction(obj: PpaSimulationMetaObject): SimulationToolbarAction | null {
    const id = obj.id?.trim();
    if (!id) return null;

    return {
        id,
        label: obj.props?.label?.trim() || id,
        isLocked: obj.constraints?.is_locked ?? false,
        required: obj.props?.required ?? obj.required ?? false,
    };
}

function mapToolbarActions(objects: PpaSimulationMetaObject[] | undefined): SimulationToolbarAction[] {
    if (!objects?.length) return [];

    return objects
        .map((obj) => (obj.id ? toToolbarAction(obj) : null))
        .filter((action): action is SimulationToolbarAction => action !== null);
}

function findSimulationFilterPanel(meta: PpaSimulationMetaResponse | undefined): PpaSimulationMetaObject | undefined {
    const toolbar = findMetaObjectById(meta?.objects, 'toolbar');
    const toolbarActions = findMetaObjectById(toolbar?.objects, 'toolbar_actions');
    const filterFromToolbar = toolbarActions?.objects?.find((obj) => obj.id === 'filter');

    return filterFromToolbar ?? findMetaObjectById(meta?.objects, 'filter');
}

function toFilterCategory(obj: PpaSimulationMetaObject): PpaFilterCategory | null {
    const id = obj.id?.trim();
    if (!id) return null;

    const isAttribute =
        obj.type?.toLowerCase() === 'attribute' || obj.props?.is_attribute === true;
    if (!isAttribute) return null;

    const label = obj.props?.label?.trim() || id;

    return {
        key: id,
        label,
        attributeType: toSimulatorFilterAttributeType(id),
        dimension: obj.props?.dimension,
        isDefaultSelected: obj.props?.is_default_selected ?? false,
    };
}

function toEditedActionType(obj: PpaSimulationMetaObject): SimulationFilterEditedAction | null {
    const inputValue = obj.props?.input_value?.trim();
    const id = obj.props?.filter_id?.trim();
    if (!inputValue || !id) return null;

    return {
        id,
        input_value: inputValue,
        label: obj.props?.label?.trim() || inputValue,
    };
}

function mapEditedActionTypes(objects: PpaSimulationMetaObject[] | undefined): SimulationFilterEditedAction[] {
    if (!objects?.length) return [];

    return objects
        .map(toEditedActionType)
        .filter((action): action is SimulationFilterEditedAction => action !== null);
}

function toShowOnlyToggle(
    obj: PpaSimulationMetaObject | undefined,
    fallbackLabel: string,
    options: { includeActionTypes?: boolean } = {},
): SimulationFilterShowOnlyItem {
    const { includeActionTypes = false } = options;

    return {
        label: obj?.props?.label?.trim() || fallbackLabel,
        defaultSelected: obj?.props?.is_default_selected ?? false,
        ...(includeActionTypes ? { actionTypes: mapEditedActionTypes(obj?.objects) } : {}),
    };
}

function toFilterFooterAction(obj: PpaSimulationMetaObject): SimulationFilterFooterAction | null {
    const id = obj.id?.trim();
    if (!id) return null;

    return {
        id,
        label: obj.props?.label?.trim() || id,
        isLocked: obj.constraints?.is_locked ?? false,
    };
}

function mapFilterFooterActions(objects: PpaSimulationMetaObject[] | undefined): SimulationFilterFooterAction[] {
    if (!objects?.length) return [];

    return objects
        .map((obj) => (obj.id ? toFilterFooterAction(obj) : null))
        .filter((action): action is SimulationFilterFooterAction => action !== null);
}

export function normalizeSimulationMetaResponse(raw: unknown): PpaSimulationMetaResponse | undefined {
    if (!raw || typeof raw !== 'object') return undefined;

    const body = raw as Record<string, unknown>;

    if (body.id === 'simulation') return body as PpaSimulationMetaResponse;
    if (body.root && typeof body.root === 'object') return normalizeSimulationMetaResponse(body.root);
    if (body.simulation && typeof body.simulation === 'object') {
        return normalizeSimulationMetaResponse(body.simulation);
    }
    if (body.data) return normalizeSimulationMetaResponse(body.data);
    if (Array.isArray(body.objects)) return body as PpaSimulationMetaResponse;

    return undefined;
}

function findToolbarSearchInputId(objects: PpaSimulationMetaObject[] | undefined): string | undefined {
    if (!objects?.length) return undefined;

    for (const obj of objects) {
        const id = obj.id?.trim();
        if (id && id.toLowerCase().replace(/-/g, '_') === 'search') {
            return id;
        }
    }

    return undefined;
}

export function parseSimulationToolbar(meta: PpaSimulationMetaResponse | undefined): SimulationToolbarConfig {
    const toolbar = findMetaObjectById(meta?.objects, 'toolbar');
    const simulationName = findMetaObjectById(toolbar?.objects, 'simulation_name');
    const toolbarActions = findMetaObjectById(toolbar?.objects, 'toolbar_actions');
    const actions = mapToolbarActions(toolbarActions?.objects);

    return {
        showSimulationName: Boolean(simulationName),
        actions,
        searchInputId: findToolbarSearchInputId(toolbarActions?.objects),
    };
}

/** Parses filter panel config from toolbar → toolbar_actions → filter → { show_only, filter_attributes, filter_footer_actions }. */
export function parseSimulationFilterConfig(meta: PpaSimulationMetaResponse | undefined): SimulationFilterConfig {
    const filterPanel = findSimulationFilterPanel(meta);

    // Prefer children under the filter button; fall back to recursive search on full meta
    // (handles APIs that omit nested objects on the filter node).
    const filterAttributes =
        findMetaObjectById(filterPanel?.objects, 'filter_attributes') ??
        findMetaObjectById(meta?.objects, 'filter_attributes');

    const showOnlySection =
        findMetaObjectById(filterPanel?.objects, 'show_only') ??
        findMetaObjectById(meta?.objects, 'show_only');

    const filterFooterActions =
        findMetaObjectById(filterPanel?.objects, 'filter_footer_actions') ??
        findMetaObjectById(meta?.objects, 'filter_footer_actions');

    const editedToggle = showOnlySection?.objects?.find((obj) => obj.id === 'edited');
    const impactedToggle = showOnlySection?.objects?.find((obj) => obj.id === 'impacted');

    const categories = (filterAttributes?.objects ?? [])
        .map(toFilterCategory)
        .filter((category): category is PpaFilterCategory => category !== null);

    return {
        categories,
        showOnly: {
            edited: toShowOnlyToggle(editedToggle, 'Edited', { includeActionTypes: true }),
            impacted: toShowOnlyToggle(impactedToggle, 'Impacted'),
        },
        footerActions: mapFilterFooterActions(filterFooterActions?.objects),
    };
}

export function getDefaultSimulationToolbarConfig(): SimulationToolbarConfig {
    return {
        showSimulationName: true,
        actions: [],
    };
}

export function getDefaultSimulationFilterConfig(): SimulationFilterConfig {
    return {
        categories: [],
        showOnly: {
            edited: { label: '', defaultSelected: false, actionTypes: [] },
            impacted: { label: '', defaultSelected: false },
        },
        footerActions: [],
    };
}

function toTableColumnMeta(
    obj: PpaSimulationMetaObject | undefined,
    options: { includeGroupChildren?: boolean } = {},
): SimulationTableColumnMeta | undefined {
    if (!obj) return undefined;
    const id = obj.id?.trim();
    if (!id) return undefined;

    const type = obj.type?.trim();
    const includeGroupChildren = options.includeGroupChildren ?? type?.toLowerCase() === 'table_columns';
    const children = includeGroupChildren
        ? (obj.objects ?? [])
              .map((child) => toTableColumnMeta(child, { includeGroupChildren: false }))
              .filter((child): child is SimulationTableColumnMeta => child !== undefined)
        : [];

    return {
        id,
        type,
        label: obj.props?.label?.trim() || id,
        placeholder: obj.props?.placeholder?.trim(),
        suffix: obj.props?.suffix?.trim(),
        valueType: obj.props?.value_type?.trim(),
        isDefaultSelected: obj.props?.is_default_selected,
        isAttribute: obj.props?.is_attribute === true || type?.toLowerCase() === 'attribute',
        editMode: obj.constraints?.edit_mode ?? obj.edit_mode,
        isLocked: obj.constraints?.is_locked ?? obj.is_locked,
        children: children.length > 0 ? children : undefined,
    };
}

function parseIntegerProp(value: unknown, fallback: number): number {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function parseColumnFilterSort(attributeColumn: PpaSimulationMetaObject | undefined): SimulationColumnFilterSortConfig {
    const filterSort = findMetaObjectById(attributeColumn?.objects, 'column_filter_sort');
    const sortGroup = findMetaObjectById(filterSort?.objects, 'column_sort');
    const filterGroup = findMetaObjectById(filterSort?.objects, 'column_filter');
    const search = findMetaObjectById(filterGroup?.objects, 'column_filter_search');
    const footer = findMetaObjectById(filterGroup?.objects, 'column_filter_footer_actions');
    const ascending = findMetaObjectById(sortGroup?.objects, 'ascending');
    const descending = findMetaObjectById(sortGroup?.objects, 'descending');
    const reset = findMetaObjectById(footer?.objects, 'column_filter_reset');
    const cancel = findMetaObjectById(footer?.objects, 'column_filter_cancel');
    const apply = findMetaObjectById(footer?.objects, 'column_filter_apply');

    return {
        enabled: Boolean(filterSort),
        sortLabel: sortGroup?.props?.label?.trim() || 'Sort',
        ascendingLabel: ascending?.props?.label?.trim() || 'Ascending',
        descendingLabel: descending?.props?.label?.trim() || 'Descending',
        filterLabel: filterGroup?.props?.label?.trim() || 'Filter',
        searchPlaceholder: search?.props?.placeholder?.trim() || search?.props?.label?.trim() || 'Search',
        resetLabel: reset?.props?.label?.trim() || 'Reset',
        cancelLabel: cancel?.props?.label?.trim() || 'Cancel',
        applyLabel: apply?.props?.label?.trim() || 'Apply',
    };
}

export function parseSimulationTableConfig(meta: PpaSimulationMetaResponse | undefined): SimulationTableConfig {
    const table = findMetaObjectById(meta?.objects, 'table');
    const columnsNode = findMetaObjectById(table?.objects, 'columns');
    const pagination = findMetaObjectById(table?.objects, 'pagination');
    const offset = findMetaObjectById(pagination?.objects, 'offset');
    const limit = findMetaObjectById(pagination?.objects, 'limit');
    const columnObjects = columnsNode?.objects ?? [];
    const attributeColumn = columnObjects.find(
        (obj) => obj.id === 'attribute_type' || obj.type?.toLowerCase() === 'attribute',
    );

    return {
        columns: columnObjects
            .map((obj) => toTableColumnMeta(obj))
            .filter((column): column is SimulationTableColumnMeta => column !== undefined),
        columnFilterSort: parseColumnFilterSort(attributeColumn),
        pagination: {
            offset: parseIntegerProp(offset?.props?.default_value, 0),
            limit: parseIntegerProp(limit?.props?.default_value, 100),
        },
    };
}

export function getDefaultSimulationTableConfig(): SimulationTableConfig {
    return {
        columns: [],
        columnFilterSort: {
            enabled: false,
            sortLabel: 'Sort',
            ascendingLabel: 'Ascending',
            descendingLabel: 'Descending',
            filterLabel: 'Filter',
            searchPlaceholder: 'Search',
            resetLabel: 'Reset',
            cancelLabel: 'Cancel',
            applyLabel: 'Apply',
        },
        pagination: {
            offset: 0,
            limit: 100,
        },
    };
}
